import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { geocodingService, SearchSuggestion } from '@/lib/mapbox-geocoding';
import { Loader2, MapPin, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AddressData {
  fullAddress: string;
  latitude?: number;
  longitude?: number;
  feature?: SearchSuggestion;
}

interface MapboxAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (address: AddressData) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const MapboxAddressInput: React.FC<MapboxAddressInputProps> = ({
  value,
  onChange,
  onAddressSelect,
  required = false,
  placeholder = "Start typing your address...",
  className = "",
  disabled = false,
}) => {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [justSelected, setJustSelected] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string>(`session-${Date.now()}`);

  /**
   * Fetch address suggestions from Mapbox
   */
  const fetchSuggestions = useCallback(async (query: string) => {
    // Don't search if query is too short or we just selected
    if (!query || query.trim().length < 3 || justSelected) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await geocodingService.suggest(query, {
        sessionId: sessionIdRef.current,
        limit: 5,
        country: 'US',
        types: ['address', 'poi', 'place', 'postcode'],
        proximity: 'ip', // Use user's IP location for proximity bias
      });

      if (results && results.length > 0) {
        setSuggestions(results);
        setOpen(true);
      } else {
        setSuggestions([]);
        setOpen(false);
      }
    } catch (error: any) {
      console.error('Address search error:', error);

      // User-friendly error messages
      if (error.message.includes('authentication')) {
        setError('Search service is not configured. Please contact support.');
      } else if (error.message.includes('network')) {
        setError('Network error. Please check your connection.');
      } else {
        setError('Unable to search addresses. Please try again.');
      }

      setSuggestions([]);
      setOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [justSelected]);

  /**
   * Handle input value changes with debouncing
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Always update the input value immediately for responsive UI
    onChange(newValue);
    setError(null);
    setJustSelected(false);

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer for API call
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300); // 300ms debounce for better UX
  }, [onChange, fetchSuggestions]);

  /**
   * Handle selection of an address from suggestions
   */
  const handleSelect = useCallback(async (suggestion: SearchSuggestion) => {
    if (!suggestion) return;

    setJustSelected(true);
    setSuggestions([]);
    setOpen(false);

    try {
      // Try to get detailed information about the selection
      const details = await geocodingService.retrieve(suggestion.mapbox_id, {
        sessionToken: sessionIdRef.current
      });

      const addressData: AddressData = {
        fullAddress: details?.full_address || suggestion.full_address || suggestion.name,
        latitude: details?.coordinates?.latitude || suggestion.coordinates?.latitude,
        longitude: details?.coordinates?.longitude || suggestion.coordinates?.longitude,
        feature: details || suggestion
      };

      // Update the input value
      onChange(addressData.fullAddress);

      // Notify parent component
      if (onAddressSelect) {
        onAddressSelect(addressData);
      }

      // Reset session for next search
      sessionIdRef.current = `session-${Date.now()}`;
    } catch (error) {
      console.error('Failed to retrieve address details:', error);

      // Fallback: use the suggestion data we already have
      const addressData: AddressData = {
        fullAddress: suggestion.full_address || suggestion.name,
        latitude: suggestion.coordinates?.latitude,
        longitude: suggestion.coordinates?.longitude,
        feature: suggestion
      };

      onChange(addressData.fullAddress);
      if (onAddressSelect) {
        onAddressSelect(addressData);
      }
    }

    // Restore focus to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange, onAddressSelect]);

  /**
   * Handle clearing the input
   */
  const handleClear = useCallback(() => {
    onChange('');
    setSuggestions([]);
    setOpen(false);
    setError(null);
    setJustSelected(false);
    sessionIdRef.current = `session-${Date.now()}`;

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      geocodingService.cancelPendingRequests();
    };
  }, []);

  /**
   * Format suggestion for display
   */
  const formatSuggestion = (suggestion: SearchSuggestion) => {
    if (suggestion.full_address) {
      return suggestion.full_address;
    }

    if (suggestion.place_formatted) {
      return `${suggestion.name}, ${suggestion.place_formatted}`;
    }

    return suggestion.name;
  };

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              value={value}
              onChange={handleInputChange}
              onFocus={() => {
                if (value && !justSelected && suggestions.length > 0) {
                  setOpen(true);
                }
              }}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              className={cn(
                "pr-20",
                error && "border-red-500 focus:ring-red-500"
              )}
              aria-label="Address input"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-invalid={!!error}
              aria-describedby={error ? "address-error" : undefined}
            />

            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}

              {value && !isLoading && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1 hover:bg-muted rounded"
                  aria-label="Clear address"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}

              <MapPin className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width]"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList>
              {suggestions.length === 0 ? (
                <CommandEmpty>
                  {isLoading ? "Searching..." : "No addresses found"}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {suggestions.map((suggestion) => (
                    <CommandItem
                      key={suggestion.mapbox_id}
                      value={suggestion.mapbox_id}
                      onSelect={() => handleSelect(suggestion)}
                      className="cursor-pointer"
                    >
                      <MapPin className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span className="text-sm">
                        {formatSuggestion(suggestion)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && (
        <p id="address-error" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};